<script setup>
import { ref, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import WhatsappChannel from 'dashboard/api/channel/whatsappChannel';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  provider: {
    type: String,
    required: true,
    validator: value => ['evolution'].includes(value),
  },
});

const { t } = useI18n();
const router = useRouter();

const POLL_INTERVAL_MS = 3000;

const status = ref('idle'); // idle | connecting | qr | error
const qrCode = ref('');
const errorMessage = ref('');
let connectionId = null;
let pollTimer = null;

const providerKey = props.provider.toUpperCase();

function stopPolling() {
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = null;
}

async function pollStatus() {
  try {
    const { data } = await WhatsappChannel.getQrConnectionStatus(
      props.provider,
      connectionId
    );
    if (data.status === 'connected') {
      stopPolling();
      router.replace({
        name: 'settings_inboxes_add_agents',
        params: { page: 'new', inbox_id: data.id },
      });
    }
  } catch (error) {
    stopPolling();
    status.value = 'error';
    errorMessage.value =
      error.response?.data?.error ||
      t('INBOX_MGMT.ADD.WHATSAPP.QR_CONNECT.ERROR');
  }
}

async function connect() {
  status.value = 'connecting';
  try {
    const { data } = await WhatsappChannel.createQrConnection(props.provider);
    connectionId = data.id;
    qrCode.value = data.qr_code;
    status.value = 'qr';
    pollTimer = setInterval(pollStatus, POLL_INTERVAL_MS);
  } catch (error) {
    status.value = 'error';
    errorMessage.value =
      error.response?.data?.error ||
      t('INBOX_MGMT.ADD.WHATSAPP.QR_CONNECT.ERROR');
    useAlert(errorMessage.value);
  }
}

onUnmounted(() => stopPolling());
</script>

<template>
  <div class="flex flex-col items-center gap-4 py-6 text-center">
    <h3 class="text-base font-medium text-n-slate-12">
      {{ t(`INBOX_MGMT.ADD.WHATSAPP.QR_CONNECT.TITLE_${providerKey}`) }}
    </h3>
    <p class="max-w-sm text-sm text-n-slate-11">
      {{ t('INBOX_MGMT.ADD.WHATSAPP.QR_CONNECT.DESCRIPTION') }}
    </p>

    <NextButton
      v-if="status === 'idle'"
      solid
      blue
      :label="t('INBOX_MGMT.ADD.WHATSAPP.QR_CONNECT.CONNECT_BUTTON')"
      @click="connect"
    />

    <Spinner v-else-if="status === 'connecting'" />

    <div v-else-if="status === 'qr'" class="flex flex-col items-center gap-3">
      <img
        :src="qrCode"
        :alt="t('INBOX_MGMT.ADD.WHATSAPP.QR_CONNECT.TITLE_' + providerKey)"
        class="w-52 h-52 rounded-lg border border-n-weak"
      />
      <div class="flex gap-2 items-center text-sm text-n-slate-11">
        <Spinner size="small" />
        {{ t('INBOX_MGMT.ADD.WHATSAPP.QR_CONNECT.WAITING') }}
      </div>
    </div>

    <div v-else-if="status === 'error'" class="flex flex-col gap-3 items-center">
      <p class="text-sm text-n-ruby-9">{{ errorMessage }}</p>
      <NextButton
        outline
        :label="t('INBOX_MGMT.ADD.WHATSAPP.QR_CONNECT.RETRY_BUTTON')"
        @click="connect"
      />
    </div>
  </div>
</template>
